import { useEffect, useRef } from 'react';
import { Search as SearchIcon, MapPin } from 'lucide-react';
import type { SearchHit } from '@boatnav/schemas';
import { useMapController } from '../map/MapShell.js';
import { useGotoStore } from '../goto/gotoStore.js';
import { usePickPointStore } from '../goto/pickPointStore.js';
import { useSearchStore, SEARCH_MIN_LEN } from './searchStore.js';
import { msg } from '../../lib/messages.js';
import './search.css';

interface SearchSheetProps {
  onClose: () => void;
}

/**
 * Place search sheet (spec 13): input + result list, debounced against the
 * API proxy. Below the 2-char minimum it shows the last 10 recent
 * selections instead — persisted in IDB, so they stay selectable offline.
 * "Velg i kartet" is the documented fallback for long-press unreliability.
 */
export function SearchSheet({ onClose }: SearchSheetProps): JSX.Element {
  const controller = useMapController();
  const setGoto = useGotoStore((s) => s.set);
  const startPickMode = usePickPointStore((s) => s.startPickMode);

  const query = useSearchStore((s) => s.query);
  const results = useSearchStore((s) => s.results);
  const recents = useSearchStore((s) => s.recents);
  const loading = useSearchStore((s) => s.loading);
  const searched = useSearchStore((s) => s.searched);
  const setQuery = useSearchStore((s) => s.setQuery);
  const loadRecents = useSearchStore((s) => s.loadRecents);
  const selectHit = useSearchStore((s) => s.selectHit);
  const reset = useSearchStore((s) => s.reset);

  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    loadRecents();
    inputRef.current?.focus();
  }, [loadRecents]);

  // Clear query/results when the sheet closes so re-opening starts fresh.
  useEffect(() => reset, [reset]);

  const pick = (hit: SearchHit): void => {
    void selectHit(hit);
    setGoto({ point: { lat: hit.lat, lon: hit.lon }, name: hit.name });
    controller?.map.setView([hit.lat, hit.lon], 13);
    onClose();
  };

  const pickInMap = (): void => {
    startPickMode();
    onClose();
  };

  const showingRecents = query.trim().length < SEARCH_MIN_LEN;
  const list = showingRecents ? recents : results;
  const showEmpty = !showingRecents && searched && !loading && results.length === 0;

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div
        className="sheet search-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={msg.search.title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet__handle" />

        <div className="search-sheet__input-row">
          <SearchIcon size={18} className="search-sheet__icon" aria-hidden="true" />
          <input
            ref={inputRef}
            className="search-sheet__input"
            type="search"
            inputMode="search"
            placeholder={msg.search.placeholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <button type="button" className="search-sheet__pick-in-map" onClick={pickInMap}>
          <MapPin size={16} aria-hidden="true" />
          {msg.search.pickInMap}
        </button>

        {showingRecents && recents.length > 0 && (
          <p className="search-sheet__section-label">{msg.search.recents}</p>
        )}

        {showEmpty ? (
          <p className="search-sheet__empty">{msg.search.empty}</p>
        ) : (
          <ul className="search-sheet__list">
            {list.map((hit) => (
              <li key={`${hit.lat},${hit.lon}`}>
                <button type="button" className="search-sheet__row" onClick={() => pick(hit)}>
                  <span className="search-sheet__name">{hit.name}</span>
                  <span className="search-sheet__meta">
                    {hit.type}
                    {hit.municipality ? ` · ${hit.municipality}` : ''}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
